import { skipToken } from '@reduxjs/toolkit/query';
import { formatDate } from '@strata/helper-functions';
import { InfoDialog } from '@strata/react-dialog';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import classNames from 'classnames';
import { type ComponentProps } from 'react';

import { type TOCDetails } from 'store/api/claimApi';
import { useGetProductsMapQuery, useGetSupplierDetailsQuery } from 'store/api/referenceApi';

interface TOCNoticeDetailsDialogProps {
    tocDetails?: TOCDetails;
    onSecondaryButtonClick: ComponentProps<typeof InfoDialog>['onClose'];
}

export const TOCNoticeDetailsDialog = ({
    tocDetails = undefined,
    onSecondaryButtonClick,
}: TOCNoticeDetailsDialogProps) => {
    const { data: productsMap } = useGetProductsMapQuery(
        tocDetails?.product_service_oid ? undefined : skipToken,
    );
    const drugName = tocDetails?.product_service_oid
        ? productsMap?.[tocDetails.product_service_oid]?.description
        : undefined;
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        tocDetails?.previous_supplier_npi
            ? {
                  npi: tocDetails?.previous_supplier_npi,
              }
            : skipToken,
    );
    return (
        <InfoDialog
            dialogTitle="Transition of Care"
            isOpen={!!tocDetails}
            onClose={onSecondaryButtonClick}
            dialogContent={
                <>
                    <div className="padding__bottom--medium">
                        The following details are about the prior fill.
                    </div>
                    <div className="sds_toc-detail__section-title">Pharmacy Information</div>
                    <div className={classNames('sds_toc-detail__row', 'padding__bottom--medium')}>
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Pharmacy Name"
                            value={supplierDetails?.name}
                        />
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Pharmacy NPI"
                            value={tocDetails?.previous_supplier_npi}
                        />
                    </div>
                    <div className={classNames('sds_toc-detail__row', 'padding__bottom--x-large')}>
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Last Fill Date"
                            value={formatDate(tocDetails?.last_fill_date)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Days Supply"
                            value={tocDetails?.drug_days_supply}
                        />
                    </div>
                    <div className="sds_toc-detail__section-title">Drug Information</div>
                    <div className={classNames('sds_toc-detail__row', 'padding__bottom--medium')}>
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Drug Name"
                            value={drugName}
                        />
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Dosage Form"
                            value={tocDetails?.dosage_form}
                        />
                    </div>
                    <div className={classNames('sds_toc-detail__row', 'padding__bottom--medium')}>
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Strength"
                            value={tocDetails?.drug_strength}
                        />
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="NDC"
                            value={tocDetails?.ndc}
                        />
                    </div>
                    <div className={classNames('sds_toc-detail__row')}>
                        <DetailLabelValuePair
                            style={{ width: '50%' }}
                            label="Quantity"
                            value={tocDetails?.quantity}
                        />
                    </div>
                </>
            }
        />
    );
};
